import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { DASH_META_ROOT } from '@revelio/auth';
import type {
  GetReportConfigQuery,
  GetUserReportsQuery,
  ReportTypeEnum,
} from '@revelio/data-access';

import { TanstackQueryError } from '../../../shared/data-fetch/tanstack-query-provider';
import { useReportRunsQuery } from '../../CustomDash/hooks/useReportRunQuery';
import { buildPathFromEntityType } from '../../CustomDash/utils/routing-helpers';
import { useUserBuiltReports } from './use-user-built-reports';

export type UnifiedReportType =
  | `${ReportTypeEnum}`
  | 'GeneratedByRevelio'
  | 'CustomReport';

type UserReportsData = GetUserReportsQuery;
type UsersType = NonNullable<UserReportsData['users']>;
type UserType = NonNullable<UsersType[number]>;
type MiddlewareReportsType = NonNullable<UserType['reports']>;
type MiddlewareReportType = NonNullable<MiddlewareReportsType[number]>;
interface CustomReportMeta {
  id: string;
  name?: string;
  link?: string;
  createdAt: string;
  updatedAt: string;
}
export interface UnifiedReport extends CustomReportMeta {
  reportType: UnifiedReportType;
  reportBuilderData?: MiddlewareReportType; // if built using report builder this data will be populated
  /** Build-a-Dash: page id of the report template this run was created from */
  customReportTemplateId?: string;
}

type UseAllReportArgs = {
  userId: string | undefined;
};
export const useAllReports = ({ userId }: UseAllReportArgs) => {
  /* Fetching report data from middleware - needed to get report status */
  const [{ data, fetching: fetchingMiddleware }, reExecuteMiddleware] =
    useUserBuiltReports(userId ? [userId] : []);

  /* Fetching report data from dashmeta - needed to get report link */
  const {
    data: userDeliverables,
    isFetching: fetchingDashmeta,
    refetch: refetchDashmeta,
  } = useQuery({
    queryKey: ['reports', userId],
    enabled: !!userId,
    queryFn: async () => {
      const requestID = crypto.randomUUID();
      const response = await fetch(
        `${DASH_META_ROOT}/api/deliverables/userdocs/${userId}`,
        {
          credentials: 'include',
          headers: {
            'request-id': requestID,
          },
        }
      );
      const responseData = await response.json();
      if (responseData.statusCode && responseData.statusCode !== 200) {
        throw new TanstackQueryError(
          `Failed to fetch reports for user ${userId}. ${responseData.message}`,
          requestID
        );
      }
      return responseData as CustomReportMeta[];
    },
  });
  const reportsFromDashmeta = useMemo(
    () => userDeliverables ?? [],
    [userDeliverables]
  );

  /* Fetch report runs from dashmeta (Build-a-Dash report runs) */
  const { data: reportRuns = [], isFetching: fetchingRuns } =
    useReportRunsQuery(userId ?? '', !!userId);

  const unifiedReports = useMemo(() => {
    if (fetchingMiddleware || fetchingDashmeta || fetchingRuns) {
      return [];
    }

    const allReportsById = (reportsFromDashmeta ?? []).reduce(
      (obj: Record<string, UnifiedReport>, item) => {
        obj[item.id] = { ...item, reportType: 'GeneratedByRevelio' }; // overwritten by reportsFromMiddleware if it exists
        return obj;
      },
      {}
    );

    const reportsFromMiddleware: MiddlewareReportsType =
      (data && data.users && data.users[0]?.reports) || [];

    reportsFromMiddleware.forEach((report) => {
      const reportType = report.report.reportType;
      if (report.uuid && allReportsById[report.uuid]) {
        // @ts-expect-error noUncheckedIndexedAccess
        allReportsById[report.uuid] = {
          ...allReportsById[report.uuid],
          reportType,
          reportBuilderData: report,
        };
      } else if (report.uuid) {
        allReportsById[report.uuid] = {
          id: report.uuid,
          name: report.name || '',
          createdAt: report.created_at || '',
          updatedAt: report.updated_at || '',
          reportType,
          reportBuilderData: report,
        };
      }
    });

    // Merge Build-a-Dash report runs (create sample/default values where missing)
    (reportRuns ?? []).forEach((run) => {
      const key = run.id || run.reportId || crypto.randomUUID();
      const name = run.name || 'Untitled Custom Report Run';
      // Keep existing if present
      if (!allReportsById[key]) {
        allReportsById[key] = {
          id: key,
          name,
          createdAt: run.created || '',
          updatedAt: run.updated || '',
          reportType: 'CustomReport',
          customReportTemplateId: run.reportId,
          // Provide a navigable link for Custom Dash runs
          link: buildPathFromEntityType('report', {
            templateId: run.reportId,
            search: run.filters ? `?filterGroups=${run.filters}` : undefined,
          }),
        } as UnifiedReport;
      }
    });

    return Object.keys(allReportsById).map(
      (reportId) => allReportsById[reportId]
    );
  }, [
    fetchingMiddleware,
    fetchingDashmeta,
    fetchingRuns,
    data,
    reportsFromDashmeta,
    reportRuns,
  ]);

  const executeQuery = useCallback(() => {
    refetchDashmeta();
    reExecuteMiddleware({ requestPolicy: 'network-only' });
  }, [reExecuteMiddleware, refetchDashmeta]);

  return {
    reports: unifiedReports,
    fetching: fetchingMiddleware || fetchingDashmeta || fetchingRuns,
    executeQuery,
  };
};

export const getUnifiedReportTypeDisplayText = (
  reportType: UnifiedReportType,
  config: GetReportConfigQuery | undefined
) => {
  const label = config?.reports_validation.find(
    (report) => report?.report_validation?.report_type === reportType
  )?.report_validation?.label;

  if (reportType === 'GeneratedByRevelio') {
    return 'Generated by Revelio Labs';
  }

  if (reportType === 'CustomReport') {
    return 'Custom Report';
  }

  return label || 'Unknown Report Type';
};
